import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import "./CaseAdminFilters.css";

import { Case } from "pwapi";

import { fetchAdminCases } from "shared/actions/admin/adminCasesActions";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { ApplicationState } from "shared/testUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { ControlledToggleButton } from "components/customInputs/buttons/ToggleButton";
import {
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetBooleanFilters,
  resetFilters,
} from "components/filtersUtils";

import CaseFilters from "../filters/CaseFilters";
import HiddenDropDown from "../filters/HiddenDropdown";
import SlidesFilters from "../filters/SlidesFilters";
import {
  Filter,
  FilterPageFunctionComponentProps,
  QueryFilter,
} from "../types/adminTypes";

type FiltersObj = {
  case: Filter;
  slide: Filter;
  query: QueryFilter;
};

const CaseAdminFilters = ({
  sortBy,
  pageSize,
  pageIndex,
  toggleAllRowsExpanded,
  setPage,
}: FilterPageFunctionComponentProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const usp = new URLSearchParams(location.search);

  const initializeFilters = (filters: any) => {
    return Object.keys(filters).reduce((acc: Filter, value) => {
      const initialFilter = usp.get(value);
      if (value === "biopsyType") {
        acc[value] = initialFilter ? initialFilter.split(",") : "";
      } else {
        acc[value] = initialFilter ? initialFilter : "";
      }
      return acc;
    }, {});
  };

  const [slideFilters, setSlideFilters] = useState<any>(
    initializeFilters({
      slidesStatus: "",
      slidesStainName: "",
      slidesLab: "",
      slidesSlideReviewReason: "",
      slidesQcStatus: "",
      slidesScannedDateAfter: "",
      slidesScannedDateBefore: "",
      slidesExpectedUploadByAfter: "",
      slidesExpectedUploadByBefore: "",

      slidesSpecimenProcedure: "",
      slidesSpecimenSource: "",
      slidesSpecimenBiopsyType: "",
    })
  );

  const [caseFilters, setCaseFilters] = useState<any>(
    initializeFilters({
      status: "",
      accession: "",
      source: "",
      grossCompletedAfter: "",
      grossCompletedBefore: "",
      signOutDateAfter: "",
      signOutDateBefore: "",
      expectedBy: "",
      biopsyType: "",
      activeAssignmentName: "",
      receivedDateAfter: "",
      receivedDateBefore: "",
      patientName: "",
      patientDateOfBirth: "",
      patientClinicName: "",
      patientAddressState: "",
      patientClinicLocationState: "",
      patientInsuranceInsuranceCompanyName: "",
      grossedByCert: "",
    })
  );

  const [queryFilters, setQueryFilters] = useState<any>({
    isDiagnostic: !!usp.get("isDiagnostic"),
    reviewed: !!usp.get("reviewed"),
    hasFinalizingSignature: !!usp.get("hasFinalizingSignature"),
  });

  // A variable to reference if the component has just mounted
  // This is intended for use to block the first case list api call on first render.
  const didMount = useRef<boolean>(false);

  useEffect(() => {
    // Do not set the search params if default params are true and the filters have just mounted
    // This is to prevent changing the location object and causing a second render
    if (
      didMount.current ||
      sortBy[0]?.id !== "received_date" ||
      pageIndex !== 0
    )
      setSearchParams({
        case: caseFilters,
        slide: slideFilters,
        query: queryFilters,
      });
  }, [sortBy, pageIndex]);

  useEffect(() => {
    const filtersAreEmpty = [
      ...Object.values(caseFilters),
      ...Object.values(slideFilters),
    ].every((value) => value === "");

    // Do not run if all the defaults are true and it just mounted for the first time.
    // If the filters are empty, the list is in its default state.
    if (
      !filtersAreEmpty ||
      (sortBy?.length > 0 && sortBy[0].id !== "received_date") ||
      pageIndex !== 0 ||
      didMount.current
    ) {
      filterCases({
        case: caseFilters,
        slide: slideFilters,
        query: queryFilters,
      });
    }

    didMount.current = true;
  }, [sortBy, pageIndex, pageSize]);

  const onChangeSlideFilter = (key: string, value: string) => {
    setSlideFilters({ ...slideFilters, [key]: value });
  };

  const onChangeCaseFilter = (key: string, value: string | string[]) => {
    setCaseFilters({ ...caseFilters, [key]: value });
  };

  const onChangeSlideDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(slideFilters, setSlideFilters, values);
  };

  const onChangeCaseDateRangeFilter = (values: { [key: string]: Date }) => {
    onChangeDateRangeFilter(caseFilters, setCaseFilters, values);
  };

  const onChangeQueryFilter = (key: string) => {
    setQueryFilters({ ...queryFilters, [key]: !queryFilters[key] });
  };

  const onChangeDateRangeFilter = (
    filters: string[],
    set: Function,
    values: { [key: string]: Date }
  ) => {
    const newState = Object.assign({}, filters, values);
    set(newState);
  };

  const reduceQueriesToApi = (filters: QueryFilter) => {
    return Object.keys(filters).reduce((acc: QueryFilter, filter: string) => {
      if (filters[filter]) {
        acc[filter] =
          filter === "reviewed" || filter === "hasFinalizingSignature"
            ? false
            : true;
      }
      return acc;
    }, {});
  };

  const setSearchParams = (filters: FiltersObj) => {
    const params = {
      ...reduceFiltersForSearchParams(filters.case),
      ...reduceFiltersForSearchParams(filters.slide),
      ...reduceQueriesToApi(filters.query),
    } as any;
    if (sortBy.length) {
      params.sortBy = sortBy.map((s) => `${s.desc ? "-" : ""}${s.id}`);
    }
    if (pageIndex > 0) {
      params.page = pageIndex + 1;
    }
    navigate(`${location.pathname}?${new URLSearchParams(params).toString()}`, {
      replace: true,
    });
  };

  const filterCases = (filters: FiltersObj, resetPage?: boolean) => {
    const grossedByCertFilter =
      (filters.case.grossedByCert as string)?.match(/,(\d*)$/)?.[1] ||
      undefined;
    const source =
      filters.query.reviewed || filters.query.hasFinalizingSignature
        ? Case.source.PW
        : filters.case.source;
    const payload = {
      limit: pageSize,
      offset: resetPage ? 0 : pageSize * pageIndex,
      ordering: [
        "-priority",
        ...sortBy.map((s) => {
          const descending = s.desc ? "-" : "";
          return s.id === "received_date"
            ? `${descending}received_date,${descending}accession_completed,${descending}created_at`
            : `${descending}${s.id}`;
        }),
      ].join(","),
      ...reduceFiltersToApi(filters.case),
      ...reduceFiltersToApi(filters.slide),
      ...reduceQueriesToApi(filters.query),
      ...(grossedByCertFilter && { grossedByCert: grossedByCertFilter }),
      ...(source && { source: source }),
    };
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
    resetPage && pageIndex !== 0
      ? setPage(0)
      : dispatch(
          fetchAdminCases({
            filters: payload,
            isManualReview: filters.slide?.slidesStatus === "manual-review",
          })
        );
  };

  const onSubmitFilter = (e: SyntheticEvent) => {
    e.preventDefault();
    setSearchParams({
      case: caseFilters,
      slide: slideFilters,
      query: queryFilters,
    });
    filterCases(
      { case: caseFilters, slide: slideFilters, query: queryFilters },
      true
    );
  };

  const onResetFilter = () => {
    const filters = {
      case: resetFilters(caseFilters),
      slide: resetFilters(slideFilters),
      query: resetBooleanFilters(queryFilters),
    };
    setSlideFilters(filters.slide);
    setCaseFilters(filters.case);
    setQueryFilters(filters.query);
    setSearchParams(filters);
  };

  return (
    <form className="AdminFilters" onSubmit={onSubmitFilter}>
      <div className="AdminFilters AdminFilters--vertical">
        <div className="AdminFilters__filters">
          <CaseFilters
            {...caseFilters}
            onChange={onChangeCaseFilter}
            onChangeDateRange={onChangeCaseDateRangeFilter}
          />
        </div>
        <HiddenDropDown label="Slide Filters" type="filter" isInitOpen={true}>
          <div className="AdminFilters__filters">
            <SlidesFilters
              {...slideFilters}
              onChange={onChangeSlideFilter}
              onChangeDateRange={onChangeSlideDateRangeFilter}
            />
          </div>
        </HiddenDropDown>
      </div>
      <div className="AdminFilters__queries">
        <ControlledToggleButton
          isToggled={queryFilters.isDiagnostic}
          onClick={() => {
            onChangeQueryFilter("isDiagnostic");
          }}
        >
          Diagnostic Cases
        </ControlledToggleButton>
        {features?.qaReviews && (
          <ControlledToggleButton
            isToggled={queryFilters.reviewed}
            onClick={() => {
              onChangeQueryFilter("reviewed");
            }}
          >
            Unreviewed Cases
          </ControlledToggleButton>
        )}
        <ControlledToggleButton
          isToggled={queryFilters.hasFinalizingSignature}
          onClick={() => {
            onChangeQueryFilter("hasFinalizingSignature");
          }}
        >
          Missing Finalizing Signature
        </ControlledToggleButton>
        <div style={{ marginTop: "auto" }} />
        <PathologyWatchButton type="submit" color="green" title="filter cases">
          Filter Cases
        </PathologyWatchButton>
        <PathologyWatchButton onClick={onResetFilter} title="reset filters">
          Reset Filters
        </PathologyWatchButton>
      </div>
    </form>
  );
};

export default CaseAdminFilters;
